// Advanced preloading utility for instant loading
class MediaPreloader {
  constructor() {
    this.cache = new Map();
    this.preloadQueue = new Set();
    this.isPreloading = false;
  }

  // Preload images with priority
  preloadImage(src, priority = 'low') {
    return new Promise((resolve, reject) => {
      if (this.cache.has(src)) {
        resolve(this.cache.get(src));
        return;
      }

      const img = new Image();
      
      // Set priority for modern browsers
      if ('fetchPriority' in img) {
        img.fetchPriority = priority;
      }

      img.onload = () => {
        this.cache.set(src, img);
        resolve(img);
      };

      img.onerror = reject;
      img.src = src;
    });
  }

  // Preload video metadata
  preloadVideo(src) {
    return new Promise((resolve, reject) => {
      if (this.cache.has(src)) {
        resolve(this.cache.get(src));
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;

      video.onloadedmetadata = () => {
        this.cache.set(src, video);
        resolve(video);
      };

      video.onerror = reject;
      video.src = src;
    });
  }

  // Batch preload multiple assets
  async batchPreload(assets) {
    const promises = assets.map(asset => {
      if (asset.type === 'image') {
        return this.preloadImage(asset.src, asset.priority || 'high');
      } else if (asset.type === 'video') {
        return this.preloadVideo(asset.src);
      }
    });

    return Promise.allSettled(promises);
  }

  // Prefetch API data
  async prefetchData(url, options = {}) {
    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    try {
      const response = await fetch(url, {
        ...options,
        priority: 'low' // Modern browsers
      });
      
      if (response.ok) {
        const data = await response.json();
        this.cache.set(url, data);
        return data;
      }
    } catch (error) {
      console.warn('Prefetch failed:', error);
    }
  }

  // Clear old cache entries
  clearCache() {
    this.cache.clear();
  }

  // Get cache size
  getCacheInfo() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Singleton instance
export const mediaPreloader = new MediaPreloader();

// Intersection Observer for lazy loading with preloading
export class LazyPreloader {
  constructor(options = {}) {
    this.options = {
      rootMargin: '50px',
      threshold: 0.1,
      ...options
    };
    
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      this.options
    );
  }

  observe(element) {
    this.observer.observe(element);
  }

  unobserve(element) {
    this.observer.unobserve(element);
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const src = element.dataset.src;
        
        if (src) {
          if (element.tagName === 'IMG') {
            mediaPreloader.preloadImage(src, 'high').then(() => {
              element.src = src;
              element.classList.add('loaded');
            });
          } else if (element.tagName === 'VIDEO') {
            element.src = src;
            element.load();
          }
        }
        
        this.unobserve(element);
      }
    });
  }
}

export const lazyPreloader = new LazyPreloader();
